/** @jsxImportSource theme-ui */
import React from 'react';
import { Button, ButtonGroup, Menu } from 'semantic-ui-react';

import {
  HighlightableSpan,
  HighlightableSpanContext,
} from 'components/_common/HighlightableSpan/HighlightableSpan';
import { Typography } from 'components/_common/Typography';

import NotFoundMessage from '../Misc/NotFoundMessage';
import { AdditiveSearchCategory, AdditiveSearchCategoryOption } from './models';

interface SearchCategoryItemsProps<T> {
  selectedId: number;
  name: string;
  options: AdditiveSearchCategoryOption<T>[];
  onSelect: (object: T) => void;
  selectedAnnotationText?: string;
  matchText?: string;
}

function SearchCategoryItems<T>({
  selectedId,
  name,
  options,
  onSelect,
  selectedAnnotationText = '(Added)',
  matchText = '',
}: React.PropsWithChildren<SearchCategoryItemsProps<T>>): React.ReactElement {
  return (
    <Menu.Item key={name}>
      <Menu.Header>{name}</Menu.Header>
      <HighlightableSpanContext.Provider
        value={{
          search: matchText,
          highlightClass: 'highlighted',
          highlightAll: false,
        }}
      >
        <Menu.Menu>
          {options.map(option => (
            <Menu.Item
              data-testid={`menu-item-${option.name}`}
              key={option.id}
              name={`${option.id}`}
              active={selectedId === option.id}
              onClick={() => onSelect(option.data)}
              disabled={option.disabled}
            >
              <HighlightableSpan text={option.name} />
              &nbsp;{option.disabled && selectedAnnotationText}
            </Menu.Item>
          ))}
        </Menu.Menu>
      </HighlightableSpanContext.Provider>
    </Menu.Item>
  );
}

type AdditiveSearchCategoryMenuProps<T> = {
  /** The search items grouped by their associated category in the menu */
  categories: AdditiveSearchCategory<T>[];
  /** Unique identifier of the currently selected value */
  selectedId?: number;
  /**
   * The label on the confirm selection button
   * @default 'Add'
   */
  addButtonLabel?: string;
  /** 
    The main text for when no results are shown
    @default 'No matching results'
   */
  noResultsText?: string;
  /**
   * The subtext displayed when no results are shown
   * @default null
   */
  noResultsSubText?: string;
  /**
   * Hint text for when there are no results
   * @default null
   */
  noResultsHintText?: string;
  /**
   * The state that the add/cancel buttons are currently in
   * @default 'enabled'
   */
  trayButtonsState?: 'enabled' | 'disabled' | 'busy';
  /**
   * Removes the tray buttons. This removes the ability to confirm or cancel selections
   * @default false
   */
  noTrayButtons?: boolean;
  /**
   * The text to show beside disabled items
   */
  selectedAnnotationText?: string;
  /**
   * The text that is being highlighted (used along with searching typically)
   */
  matchText?: string;
  /** Fires when an item is selected */
  onSelect: (object: T) => void;
  /** Fires when the confirm button is clicked */
  onAdd?: () => void;
  /** Fires when the cancel button is clicked */
  onCancel?: () => void;
};
export function AdditiveSearchCategoryMenu<T>({
  categories,
  selectedId,
  addButtonLabel = 'Add',
  noResultsText = 'No matching results',
  noResultsSubText = null,
  noResultsHintText = null,
  trayButtonsState = 'enabled',
  noTrayButtons = false,
  selectedAnnotationText,
  matchText,
  onSelect,
  onAdd,
  onCancel,
}: React.PropsWithChildren<AdditiveSearchCategoryMenuProps<T>>): React.ReactElement {
  const hasMatchingResults = !!categories.length;

  return (
    <Menu
      vertical
      attached
      fluid
      sx={{
        m: '0 !important',
        border: '0 !important',
      }}
    >
      {hasMatchingResults &&
        categories.map(
          ({ name, values }) =>
            values.length && (
              <SearchCategoryItems<T>
                key={name}
                selectedId={selectedId}
                onSelect={onSelect}
                name={name}
                options={values}
                selectedAnnotationText={selectedAnnotationText}
                matchText={matchText}
              />
            )
        )}

      {!hasMatchingResults && (
        <Menu.Item>
          <NotFoundMessage
            text={noResultsText}
            subText={noResultsSubText}
            hintText={noResultsHintText}
          />
        </Menu.Item>
      )}

      {hasMatchingResults && !noTrayButtons && (
        <ButtonGroup
          className="additive-search"
          sx={{
            display: 'flex !important',
            position: 'sticky',
            bottom: '0',
            '.button': {
              borderBottom: t => [`1px solid ${t.colors.deepSea40}`, null],
            },
          }}
        >
          <Button disabled={trayButtonsState === 'busy'} onClick={onCancel}>
            <Typography variant="button" inline>
              Cancel
            </Typography>
          </Button>
          <Button
            data-testid="searchAddButton"
            className="add-button"
            loading={trayButtonsState === 'busy'}
            disabled={trayButtonsState !== 'enabled' || !hasMatchingResults}
            onClick={onAdd}
          >
            <Typography variant="button" inline>
              {addButtonLabel}
            </Typography>
          </Button>
        </ButtonGroup>
      )}
    </Menu>
  );
}
